import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Shift-click and Cmd/Ctrl-click across the tiles of one board.
 *
 * The selection belongs to the board you're looking at and nothing else. Walk
 * into a section, filter the room, or delete what was chosen, and it empties —
 * a selection you can't see is a selection you'll act on by accident.
 */

/** What a click carries. Narrowed so this doesn't need a React event type. */
export type Modifiers = {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
};

export type Selection = {
  ids: readonly string[];
  count: number;
  has: (id: string) => boolean;
  /**
   * True when the click was a selection gesture and the caller should do
   * nothing else with it — no navigation, no panel.
   */
  click: (id: string, modifiers: Modifiers) => boolean;
  clear: () => void;
};

export function useSelection(ordered: readonly string[]): Selection {
  const [chosen, setChosen] = useState<readonly string[]>([]);
  /** Where a shift-range counts from: the last thing clicked on its own. */
  const anchor = useRef<string | undefined>(undefined);

  // Ids come and go — deleted, moved to another room, filtered out by a
  // search. Anything no longer on the board drops out of the selection.
  const present = useMemo(() => new Set(ordered), [ordered]);

  useEffect(() => {
    if (ordered.length === 0) return;
    setChosen((was) => {
      const kept = was.filter((id) => present.has(id));
      return kept.length === was.length ? was : kept;
    });
  }, [ordered, present]);

  const clear = useCallback(() => {
    anchor.current = undefined;
    setChosen((was) => (was.length === 0 ? was : []));
  }, []);

  const click = useCallback(
    (id: string, modifiers: Modifiers) => {
      const range = modifiers.shiftKey;
      const toggle = modifiers.metaKey || modifiers.ctrlKey;

      if (!range && !toggle) {
        // An ordinary click means "just this one, and open it" — so the
        // selection goes away and the tile's own link does its job.
        clear();
        return false;
      }

      if (toggle) {
        anchor.current = id;
        setChosen((was) =>
          was.includes(id) ? was.filter((each) => each !== id) : [...was, id],
        );
        return true;
      }

      // Shift with nothing to count from behaves like the first click of a
      // range rather than doing nothing.
      const from = anchor.current === undefined ? undefined : ordered.indexOf(anchor.current);
      const to = ordered.indexOf(id);
      if (from === undefined || from === -1 || to === -1) {
        anchor.current = id;
        setChosen([id]);
        return true;
      }

      // The anchor stays put, so dragging the shift key back and forth grows
      // and shrinks one range instead of leaving a trail of them.
      const span = ordered.slice(Math.min(from, to), Math.max(from, to) + 1);
      setChosen(span);
      return true;
    },
    [ordered, clear],
  );

  return {
    ids: chosen,
    count: chosen.length,
    has: (id) => chosen.includes(id),
    click,
    clear,
  };
}
